import { memo } from 'react';
import {
  ArrowUp, BookOpen, MousePointer2,
} from 'lucide-react';
import { WALLPAPER } from '../art';
import {
  ADAPTIVE, BRIDGE, COURSE, DRAFT, EXPLAIN, LINKED_CHAPTERS, LINKS, MAX_LEVEL, PLACEHOLDER, PLACEHOLDER_SHORT,
  PROGRESS, QUESTION, RESULTS, STEPS,
} from '../storyScript';

/* The scene tree: one DOM for all five steps. Every animated node carries
   data-a="<id>"; the stage finds them once and writes transform / opacity from
   the scene timeline each frame. Nothing here is keyed by step or run, so
   nothing ever remounts.

   Look (owner, 2026-09-29): plain white page, cards in the teacher
   dashboard's style (white, hairline border, soft shadow), and the product's
   own Buffy composer (Assignments chat) with his line as plain text above it.
   Color only as small accents inside the UI. ?frame=tile adds a rounded
   wallpaper tile behind the cards (kept for comparison; off by default).

   Two layers (hero/layout.js): the ARTBOARD (the step's cards; artboard px,
   scaled by k, centered in the stage) and the BAND (the chat group, the
   dragged file and its flying icon; stage px). Buffy is not in here:
   BuffyActor draws him beside the composer, following the chat group. */

const W_KEYS = ['w1', 'w2', 'w3', 'w4', 'w5'];

const rect = (r) => ({ left: r.x, top: r.y, width: r.w, height: r.h });
// a level on the 1 to 7 scale as a bar length, and as text ("2.6")
const pctOf = (level) => `${((level / MAX_LEVEL) * 100).toFixed(2)}%`;
const lv = (n) => n.toFixed(1);

function Wallpaper({ which }) {
  const art = WALLPAPER[which] || {};
  if (art.tile) return <img className="hx-bg-img" src={art.tile} alt="" draggable="false" />;
  // art not picked yet: a gradient in the same hue keeps the page finished
  const hue = art.hue || '#A9A6F2';
  const soft = art.soft || '#EDEBFF';
  return <div className="hx-bg-img" style={{ background: `linear-gradient(135deg, ${hue} 0%, ${soft} 55%, ${hue} 100%)` }} />;
}

function Tile({ L }) {
  return (
    <div className="hx-tilebox" style={rect(L.tile)}>
      {W_KEYS.map((w, i) => (
        <div key={w} data-a={`sh-${i + 1}`} className={`hx-tile-sh hx-sh-${w}`} style={{ borderRadius: L.radius }} />
      ))}
      <div className="hx-tile" style={{ borderRadius: L.radius }}>
        <div data-a="far" className="hx-far">
          {W_KEYS.map((w, i) => (
            <div key={w} data-a={`bg-${i + 1}`} className="hx-bg"><Wallpaper which={w} /></div>
          ))}
        </div>
        <div data-a="bright" className="hx-bright" />
      </div>
    </div>
  );
}

/* Step 1's connectors, in the "Any curriculum" section's look (sections.css
   .cxs-link): straight 2 px lines in the math hue, a white start dot with a
   blue ring, drawn row to chip: one per linked chapter (all 10, owner). */
function Links({ L, paths, phone }) {
  if (phone) return null;
  return (
    <svg className="hx-strings" width={L.W} height={L.H} viewBox={`0 0 ${L.W} ${L.H}`} aria-hidden="true">
      <g data-a="links">
        {LINKS.map((lk, idx) => idx + 1).filter((i) => paths[`s${i}-dot`]).map((i) => (
          <g key={i}>
            <path data-a={`s${i}-line`} className="hx-link-line" d={paths[`s${i}-line`]} pathLength="100" />
            <circle data-a={`s${i}-dot`} className="hx-link-dot" cx={paths[`s${i}-dot`].x} cy={paths[`s${i}-dot`].y} r="3.5" />
          </g>
        ))}
      </g>
    </svg>
  );
}

function Typed({ id, caretId }) {
  return (
    <span data-a={id} className="hx-typed">
      <span className="hx-typed-on" />
      {caretId && <i data-a={caretId} className="hx-caret" />}
      <span className="hx-typed-ghost" />
    </span>
  );
}

/* Buffy's replies: plain text above the composer (the product's chat
   reply), typed at his pace. In the dock the reply is his line. */
const REPLIES = [
  ...STEPS.map((st, i) => ({ id: `reply-${i + 1}`, text: st.buffy })),
  { id: 'reply-4b', text: STEPS[3].after },
];

function Replies({ band }) {
  const c = band.caption;
  return (
    <div className="hx-caption" style={{ left: c.x, top: c.y, width: c.w, height: c.h, fontSize: c.font, lineHeight: `${c.lh}px` }}>
      {REPLIES.map((r) => (
        <p key={r.id} data-a={r.id} className="hx-cap">
          <span data-a={`${r.id}-t`} className="hx-cap-t">
            <span className="hx-typed-on" />
            <span className="hx-typed-ghost">{r.text}</span>
          </span>
        </p>
      ))}
    </div>
  );
}

/* The teacher's sent messages: right-aligned bubbles (the product chat, as
   in the Ask Buffy section). Step 1's carries the file. */
function Bubbles({ band, phone }) {
  const b = band.bubble;
  const box = {
    right: band.stageW - b.right, bottom: band.h - b.bottom, maxWidth: b.maxW, padding: `${b.padY}px ${b.padX}px`, fontSize: b.font, lineHeight: `${b.lh}px`,
  };
  return STEPS.map((st, i) => (
    <div key={st.id} data-a={`bubble-${i + 1}`} className="hx-bubble" style={box}>
      {i === 0 && <FileChip className="hx-file--chip hx-bubble-file" phone={phone} />}
      <span className="hx-bubble-t">{st.teacher}</span>
    </div>
  ));
}

/* The product's composer (BuffyComposer, Assignments): a white rounded box,
   1px border, soft shadow, the product placeholder, a dark send button. Big
   and fixed (owner: "make that box bigger"): 64 px tall with 18 px text on
   desktop, 56 px with 16 px on a phone; larger still in beats A and B (the
   chat group's scale). Step 1's file sits in it before it is sent. */
function SendButton({ c, bottom }) {
  // on the text row: vertically centered in the last `c.h` px of the box,
  // inside its 1 px border, exactly where the normal box centers it (no jump
  // when the two boxes swap)
  const style = bottom
    ? { width: c.send, height: c.send, top: 'auto', bottom: (c.h - 2 - c.send) / 2 }
    : { width: c.send, height: c.send, marginTop: -c.send / 2 };
  return (
    <span data-a="send" className="hx-send" style={style}>
      <span className="hx-send-face hx-send-idle"><ArrowUp strokeWidth={2.2} /></span>
      <span data-a="send-live" className="hx-send-face hx-send-live"><ArrowUp strokeWidth={2.2} /></span>
    </span>
  );
}

function Composer({ band, phone }) {
  const c = band.composer;
  // with a file attached the box is taller (like the product chat): the file
  // on its own row inside, the words on the text row under it, left-aligned
  // at the placeholder's x. Drawn natively at both heights; the switch is a
  // short FLIP (sceneTimeline 'ctall'), so at rest nothing is scaled.
  const tall = { x: c.x, y: c.y - c.attachH, w: c.w, h: c.h + c.attachH };
  const textRow = { top: c.attachH, bottom: 0 };
  return (
    <>
      <div data-a="cnorm" className="cx-surface hx-composer" style={{ ...rect(c), fontSize: c.font }}>
        <i data-a="ring" className="hx-composer-focus" />
        <span data-a="ph" className="hx-ph" style={{ left: c.pad, right: c.send + 20 }}>{phone ? PLACEHOLDER_SHORT : PLACEHOLDER}</span>
        {STEPS.map((st, i) => (i === 0 ? null : (
          <span key={st.id} data-a={`typed-${i + 1}`} className="hx-typed-box" data-over="false" style={{ left: c.pad, right: c.send + 24 }}>
            <Typed id={`typed-${i + 1}-t`} caretId={`caret-${i + 1}`} />
          </span>
        )))}
        <SendButton c={c} />
      </div>
      <div data-a="ctall" className="hx-composer hx-composer--tall" style={{ ...rect(tall), fontSize: c.font }}>
        {/* the white card (and its halo) is the only layer that grows */}
        <span data-a="ctall-bg" className="cx-surface hx-composer-bg">
          <i data-a="ring" className="hx-composer-focus" />
        </span>
        <FileChip dataA="cchip" className="hx-file--chip hx-cchip" style={{ left: c.pad, top: 12, maxWidth: c.chipW }} phone={phone} />
        <span data-a="typed-1" className="hx-typed-box" data-over="false" style={{ ...textRow, left: c.pad, right: c.send + 24 }}>
          <Typed id="typed-1-t" caretId="caret-1" />
        </span>
        <SendButton c={c} bottom />
      </div>
    </>
  );
}

/* The chat group: messages and composer, drawn TWICE at native sizes (owner
   2026-09-29: "the chat box is blurry when it gets bigger"): the dock copy
   at normal size, and the big copy in the middle of the stage laid out
   natively larger (CSS zoom, so text and borders rasterize at their real
   size). The easing between them is FLIP style: only the big copy moves,
   scaling DOWN toward the dock, and at rest each copy sits at transform none.
   Both copies carry the same data-a ids, so they type and fade together. */
function ChatBody({ band, phone }) {
  return (
    <>
      <Bubbles band={band} phone={phone} />
      <Replies band={band} />
      <Composer band={band} phone={phone} />
    </>
  );
}

function Chat({ geo }) {
  const { band } = geo;
  const phone = geo.kind === 'phone';
  const { origin: o, sc, dy } = band.chat;
  // the big copy: dock coords p land at o + (p - o) x sc + (0, dy)
  const big = { left: o.x * (1 - sc), top: o.y * (1 - sc) + dy };
  return (
    <>
      <div data-a="chat-dock" className="hx-chat" style={{ width: geo.stageW, height: band.h }}>
        <ChatBody band={band} phone={phone} />
      </div>
      <div
        data-a="chat-big"
        className="hx-chat hx-chat--big"
        style={{ ...big, width: geo.stageW * sc, height: band.h * sc, transformOrigin: `${o.x * sc}px ${o.y * sc}px` }}
      >
        <div className="hx-chat-zoom" style={{ zoom: sc, width: geo.stageW, height: band.h }}>
          <ChatBody band={band} phone={phone} />
        </div>
      </div>
    </>
  );
}

/* The teacher's file: its file icon (the one icon the owner keeps in the
   hero), name and kind. `compact` shows the icon and the kind only (the
   phone composer). The kind is never clipped: the name gives way. */
function FileChip({
  className = '', dataA, style, compact = false, phone = false,
}) {
  return (
    <div data-a={dataA} className={`hx-file ${compact ? 'hx-file--compact ' : ''}${className}`} style={style}>
      <span className="hx-file-ico"><BookOpen strokeWidth={2} /></span>
      {!compact && <span className="hx-file-name">{phone ? COURSE.fileShort : COURSE.file}</span>}
      <span className="hx-file-kind">{COURSE.fileKind}</span>
    </div>
  );
}

/* Step 1: the course. As Buffy reads it, each chapter that carries an SAT
   skill turns its "Ch N" from gray to the math blue (a plain text color
   change, no icons: owner); a chapter with no SAT skill stays gray. */
function Course({ L }) {
  const shown = COURSE.chapters.slice(0, L.course.show);
  const more = COURSE.chapters.length - shown.length;
  return (
    <div data-a="course" className="cx-surface hx-win hx-course" style={rect(L.course)}>
      <i data-a="course-ring" className="hx-accent" />
      <div className="hx-bar" style={{ height: L.course.bar }}>
        <span className="hx-bar-title">{COURSE.name}</span>
        <span className="hx-bar-meta">{`${COURSE.chapters.length} chapters`}</span>
      </div>
      <ol className="hx-rows" style={{ top: L.course.rowTop }}>
        {shown.map((c, i) => {
          const linked = LINKED_CHAPTERS.includes(i + 1);
          return (
            <li key={c} data-a={`row-${i + 1}`} className={`hx-row${linked ? '' : ' is-off'}`} style={{ height: L.course.rowH }}>
              <span className="hx-ch hx-ch--read">
                {`Ch ${i + 1}`}
                {linked && <span data-a={`tick-${i + 1}`} className="hx-ch-on">{`Ch ${i + 1}`}</span>}
              </span>
              <span className="hx-row-t">{c}</span>
            </li>
          );
        })}
        {more > 0 && (
          <li className="hx-row hx-row--more" style={{ height: L.course.rowH }}>{`+ ${more} more chapters`}</li>
        )}
      </ol>
    </div>
  );
}

/* Step 1, desktop: the SAT skills window beside the course, a chip on the
   row of each connected chapter (the section's .cx-chip look). */
function Skills({ L, phone }) {
  if (phone) return null;
  const r = L.skills;
  const rowY = (n) => L.course.rowTop + L.course.rowH * (n - 1) + L.course.rowH / 2;
  return (
    <div data-a="skills" className="cx-surface hx-win hx-skills" style={rect(r)}>
      <div className="hx-bar" style={{ height: r.bar }}>
        <span className="hx-bar-title">SAT skills</span>
        <span className="hx-bar-meta">{`in ${LINKS.length} of ${COURSE.chapters.length} chapters`}</span>
      </div>
      {/* one chip per linked chapter, centered on its chapter's row */}
      {LINKS.map((b, i) => (
        <span key={b.skill} data-a={`chip-${i + 1}`} className="hx-skill" style={{ top: rowY(b.chapter) - 8 }}>
          <i className="hx-skill-dot" style={{ background: b.dot }} />
          <span className="hx-skill-t">{b.skill}</span>
        </span>
      ))}
    </div>
  );
}

/* Step 1, phone: the SAT skills list (F1's phone list, compact): every
   linked chapter, its number, then its skill as a dot and plain text (a long
   name wraps, never cut). No connector lines. */
function Linked({ L, phone }) {
  if (!phone) return null;
  const r = L.linked;
  return (
    <div data-a="linked" className="cx-surface hx-win hx-linked" style={rect(r)}>
      <div className="hx-bar" style={{ height: r.bar }}>
        <span className="hx-bar-title">SAT skills</span>
        <span className="hx-bar-meta">{`in ${LINKS.length} of ${COURSE.chapters.length} chapters`}</span>
      </div>
      {/* the list is centered below the bar: equal room above the first row
          and below the last, whether or not a long name wraps */}
      <ul className="hx-lrows" style={{ top: r.bar, gap: r.gap }}>
        {LINKS.map((b, i) => (
          <li key={b.skill} data-a={`lrow-${i + 1}`} className="hx-lrow">
            <span className="hx-ch">{b.chapter}</span>
            <i className="hx-skill-dot" style={{ background: b.dot }} />
            <span className="hx-lrow-skill">{b.skill}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Draft({ L, phone }) {
  return (
    <div data-a="draft" className="cx-surface hx-win hx-draft" style={rect(L.draft)}>
      <i data-a="draft-ring" className="hx-accent" />
      <div className="hx-bar">
        <span className="hx-bar-title hx-bar-title--muted">{DRAFT.bar}</span>
        <span data-a="tag-draft" className="hx-pilltag">{DRAFT.draft}</span>
      </div>
      <div className="hx-draft-body">
        <div className="hx-draft-title">{DRAFT.title}</div>
        <ul className="hx-draft-rows">
          <li>{DRAFT.rows[0]}</li>
          {!phone && <li>{DRAFT.rows[1]}</li>}
        </ul>
      </div>
      {/* the footer (owner 2026-09-30, "bad spacing": the button sat 1-2 px
          under the last row's divider): its own strip under a divider, the
          class (the due date on a phone) on the left, Assign on the right,
          the same space above and below the button */}
      <div className="hx-draft-foot">
        <span className="hx-draft-foot-t">{phone ? DRAFT.rows[1] : DRAFT.klass}</span>
      </div>
      <span data-a="assign-btn" className="hx-assign">
        <i data-a="assign-ring" className="hx-assign-ring" />
        <span data-a="assign-a" className="cx-action hx-assign-face" aria-hidden="true">{DRAFT.assign}</span>
        <span data-a="assign-b" className="cx-action is-done hx-assign-face" aria-hidden="true">{DRAFT.done}</span>
      </span>
    </div>
  );
}

/* Step 2: the class's average level on each connected skill (1 to 7, the
   Assessments section's scale); the lowest row is the one to look at. */
function Results({ L, phone }) {
  const r = L.results;
  return (
    <div data-a="results" className="cx-surface hx-win hx-results" style={rect(r)}>
      <i data-a="results-ring" className="hx-accent" />
      <div className="hx-bar" style={{ height: r.bar }}>
        <span className="hx-bar-title">{RESULTS.title}</span>
        <span className="hx-bar-meta">{RESULTS.meta}</span>
      </div>
      <ul className="hx-rrows" style={{ top: r.rowTop }}>
        {RESULTS.rows.map((row, i) => {
          const b = BRIDGE.find((x) => x.chapter === row.chapter) || BRIDGE[i];
          return (
            <li key={row.chapter} className={`hx-rrow${row.weak ? ' is-weak' : ''}`} style={{ height: r.rowH }}>
              {row.weak && <i data-a="weak-fill" className="hx-rrow-fill" />}
              <span className="hx-rrow-head">
                <span className="hx-ch">{`Ch ${row.chapter}`}</span>
                <span className="hx-rrow-t">{phone ? b.short : b.skill}</span>
                {row.weak && <span data-a="weak-tag" className="hx-weak-tag">{RESULTS.weakLabel}</span>}
              </span>
              <span className="hx-rrow-bar">
                <span className="hx-rtrack"><i data-a={`rbar-${i + 1}`} className="hx-rbar" style={{ width: pctOf(row.level) }} /></span>
                <span className="hx-rpct">{lv(row.level)}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* Step 4, beside the draft: the adaptive practice every student also gets. */
function Adaptive({ L }) {
  return (
    <div data-a="adaptive" className="cx-surface hx-win hx-adaptive" style={rect(L.adaptive)}>
      <span className="hx-adaptive-head">
        <span className="hx-adaptive-t">{ADAPTIVE.title}</span>
        <span data-a="adaptive-on" className="hx-on">{ADAPTIVE.state}</span>
      </span>
      <span className="hx-adaptive-line">{ADAPTIVE.line}</span>
    </div>
  );
}

/* Step 5: the same skills 4 weeks later. ONE bar per row on the 1 to 7
   scale: a neutral track, the start level as a light segment, the gain to
   the end level as the accent segment (it grows in), and "Level a → b". */
function Progress({ L, phone }) {
  const r = L.progress;
  return (
    <div data-a="progress" className="cx-surface hx-win hx-progress" style={rect(r)}>
      <i data-a="progress-ring" className="hx-accent" />
      <div className="hx-bar" style={{ height: r.bar }}>
        <span className="hx-bar-title">{PROGRESS.title}</span>
        <span className="hx-bar-meta">{PROGRESS.meta}</span>
      </div>
      <ul className="hx-rrows" style={{ top: r.rowTop }}>
        {PROGRESS.rows.map((row, i) => {
          const b = BRIDGE.find((x) => x.chapter === row.chapter) || BRIDGE[i];
          return (
            <li key={row.chapter} className={`hx-rrow hx-prow${row.best ? ' is-best' : ''}`} style={{ height: r.rowH }}>
              {row.best && <i data-a="best-fill" className="hx-rrow-fill hx-rrow-fill--best" />}
              <span className="hx-rrow-head">
                <span className="hx-ch">{`Ch ${row.chapter}`}</span>
                <span className="hx-rrow-t">{phone ? b.short : b.skill}</span>
                <span className="hx-level">{`Level ${lv(row.from)} → ${lv(row.to)}`}</span>
              </span>
              <span className="hx-rtrack">
                <i className="hx-gain-from" style={{ width: pctOf(row.from) }} />
                <i data-a={`gain-${i + 1}`} className="hx-gain" style={{ left: pctOf(row.from), width: pctOf(row.to - row.from) }} />
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* Step 3's phone look: side by side (is-split) from 360 px, stacked under
   it (L.whyStack, is-stacked). */
const whyMode = (L, phone) => {
  if (!phone) return '';
  return L.whyStack ? ' is-stacked' : ' is-split';
};

function Question({ L, phone }) {
  return (
    <div data-a="question" className={`cx-surface hx-win hx-question${whyMode(L, phone)}`} style={rect(L.question)}>
      <i data-a="question-ring" className="hx-accent" />
      <div className="hx-bar">
        <span className="hx-bar-title hx-bar-title--muted">{QUESTION.bar}</span>
        <span className="hx-bar-meta">{QUESTION.topic}</span>
      </div>
      <div className="hx-q-body">
        {/* the equation never breaks across lines; the question may wrap
            after it (phone halves) */}
        <div className="hx-q-stem">
          <span className="hx-q-eq">{QUESTION.stem.slice(0, QUESTION.stem.indexOf('. ') + 1)}</span>
          {` ${QUESTION.stem.slice(QUESTION.stem.indexOf('. ') + 2)}`}
        </div>
        <ul className="hx-choices">
          {QUESTION.choices.map((c, i) => (
            <li key={c.key} className={`hx-choice${c.key === 'B' ? ' is-b' : ''}${c.correct ? ' is-c' : ''}`}>
              {c.key === 'B' && <i data-a="bfill" className="hx-bfill" />}
              <span className="hx-choice-key">{c.key}</span>
              <span className="hx-choice-v">{c.value}</span>
              <span className="hx-choice-track">
                <i data-a={`bar-${i + 1}`} className="hx-choice-bar" style={{ width: `${Math.round(c.share * 100)}%` }} />
                {c.key === 'B' && <i data-a="bamber" className="hx-choice-bar hx-choice-bar--amber" style={{ width: `${Math.round(c.share * 100)}%` }} />}
              </span>
              <span className="hx-choice-note">
                {c.key === 'B' && <span data-a="most" className="hx-most">{QUESTION.most}</span>}
                {c.correct && (
                  <span data-a="check-c" className="hx-correct">{QUESTION.correct}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* Step 3, right half: Buffy's explanation of the example on the left. */
function Explain({ L, phone }) {
  return (
    <div data-a="explain" className={`cx-surface hx-win hx-explain${whyMode(L, phone)}`} style={rect(L.explain)}>
      {/* stacked (narrow phones): under the example, "Buffy" leads the line */}
      {phone && L.whyStack ? (
        <span className="hx-explain-t"><span className="hx-explain-label">{EXPLAIN.label}</span>{EXPLAIN.text}</span>
      ) : (
        <>
          <span className="hx-explain-label">{EXPLAIN.label}</span>
          <span className="hx-explain-t">{EXPLAIN.text}</span>
        </>
      )}
      <span data-a="why" className="hx-why-math">
        {QUESTION.whyFrom}
        <span className="hx-why-arrow"> → </span>
        {QUESTION.whyTo}
        <span data-a="why-miss" className="hx-why-miss">{QUESTION.whyMiss}</span>
      </span>
      {!(phone && L.whyStack) && (
        <span className="hx-explain-next">
          <span className="hx-explain-next-l">{EXPLAIN.next}</span>
          <span className="hx-explain-next-t">{phone ? EXPLAIN.nextShort : EXPLAIN.nextLine}</span>
        </span>
      )}
    </div>
  );
}

function Scene({ geo, frame, scene }) {
  const { L, band } = geo;
  const phone = L.kind === 'phone';
  // the 12 px floor for scene text (qa-formatting #11): when the scene is
  // scaled down, 12.2 px, so rounding never lands a label at 11.9
  const fmin = geo.k >= 0.99 ? 12 / geo.k : Math.ceil(1220 / geo.k) / 100;
  return (
    <>
      <div className={`hx-art${!phone && geo.k < 0.9 ? ' is-dense' : ''}`} style={{ left: geo.artX, width: L.W, height: L.H, transform: `scale(${geo.k})`, '--hx-fmin': `${fmin.toFixed(2)}px` }}>
        {frame === 'tile' && <Tile L={L} />}

        <Course L={L} />
        <Skills L={L} phone={phone} />
        <Links L={L} paths={scene.paths} phone={phone} />
        <Linked L={L} phone={phone} />
        <Results L={L} phone={phone} />
        <Question L={L} phone={phone} />
        <Explain L={L} phone={phone} />
        <Draft L={L} phone={phone} />
        <Adaptive L={L} />
        <Progress L={L} phone={phone} />

        <div data-a="cursor" className="hx-cursor">
          <MousePointer2 className="hx-cursor-arrow" fill="#141414" color="#ffffff" strokeWidth={1.5} />
        </div>
      </div>
      <div className="hx-band" style={{ top: band.top, width: geo.stageW, height: band.h }}>
        <Chat geo={geo} />
        <div data-a="cursorf" className="hx-cursor">
          <FileChip dataA="dragcard" className="cx-surface hx-file--drag" phone={phone} />
          <MousePointer2 className="hx-cursor-arrow" fill="#141414" color="#ffffff" strokeWidth={1.5} />
        </div>
      </div>
    </>
  );
}

export default memo(Scene);
